import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = no-auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // NOTE: JWT in localStorage is intentional per agreed architecture
    // (Bearer-token auth, no cookie auth). Mitigated by short token TTL +
    // strict CSP. Move to httpOnly cookies only when migrating to cookie auth.
    const token = localStorage.getItem("trent_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("trent_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password, consent = false, phone = "") => {
    const { data } = await client.post("/auth/register", { name, email, password, consent, phone });
    localStorage.setItem("trent_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("trent_token");
    setUser(false);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
